import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons';
import { GTFonts, useTheme, withOpacity, type Theme } from '../../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../../utils/libs';

/**
 * Step 4: Sale loop (US2, T045). Accessible Product → Basket → Checkout →
 * Transaction sequence with a light business-type example. Purely
 * educational; behavior never changes per business type.
 */

export interface SaleLoopStepProps {
  /** Raw business type from business info, e.g. "Restaurant". */
  businessType?: string;
}

const SALE_STEPS: Array<{ icon: string; title: string; description: string }> =
  [
    {
      icon: 'pricetag-outline',
      title: 'Product',
      description: 'Pick what the customer is buying.',
    },
    {
      icon: 'basket-outline',
      title: 'Basket',
      description: 'Items collect here with quantities and totals.',
    },
    {
      icon: 'card-outline',
      title: 'Checkout',
      description: 'Take payment by cash, card, or Mpesa(where available).',
    },
    {
      icon: 'receipt-outline',
      title: 'Transaction',
      description: 'The completed sale is recorded with a receipt.',
    },
  ];

/** Light copy tailoring only; never alters behavior. */
export const getSaleLoopExample = (businessType?: string): string => {
  const type = String(businessType || '').toLowerCase();
  if (type.includes('restaurant') || type.includes('food')) {
    return 'For example: a customer orders two sodas and a plate of chips — add them to the Basket, take payment at Checkout, and the sale is recorded.';
  }
  if (type.includes('salon') || type.includes('barber')) {
    return 'For example: a client pays for a haircut and a hair product — add both to the Basket, take payment at Checkout, and the sale is recorded.';
  }
  if (type.includes('pharmacy') || type.includes('chemist')) {
    return 'For example: a customer buys painkillers and a bandage — add them to the Basket, take payment at Checkout, and the sale is recorded.';
  }
  return 'For example: a customer buys two items — add them to the Basket, take payment at Checkout, and the sale is recorded.';
};

const SaleLoopStep: React.FC<SaleLoopStepProps> = ({ businessType }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <View style={styles.container}>
      <View
        style={styles.sequence}
        accessible
        accessibilityLabel="Every sale follows the same loop: Product, then Basket, then Checkout, then Transaction"
        testID="sale-loop-sequence">
        {SALE_STEPS.map((step, index) => (
          <React.Fragment key={step.title}>
            <View style={styles.sequenceStep}>
              <View style={styles.stepIcon}>
                <Ionicons
                  name={step.icon as any}
                  size={iconSize(20)}
                  color={theme.colors.successEmerald}
                />
              </View>
              <Text style={styles.stepTitle}>{step.title}</Text>
              <Text style={styles.stepDescription}>{step.description}</Text>
            </View>
            {index < SALE_STEPS.length - 1 ? (
              <View style={styles.connector} importantForAccessibility="no">
                <Ionicons
                  name="arrow-down"
                  size={iconSize(16)}
                  color={theme.colors.successEmerald}
                />
              </View>
            ) : null}
          </React.Fragment>
        ))}
      </View>

      <Text style={styles.example} testID="sale-loop-example">
        {getSaleLoopExample(businessType)}
      </Text>
    </View>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      gap: v(16),
    },
    sequence: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderSecondary,
      padding: s(16),
    },
    sequenceStep: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: s(10),
    },
    stepIcon: {
      width: iconSize(36),
      height: iconSize(36),
      borderRadius: iconSize(18),
      backgroundColor: withOpacity(theme.colors.successEmerald, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
    },
    stepTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
      minWidth: s(96),
    },
    stepDescription: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    connector: {
      paddingVertical: v(4),
      paddingLeft: s(10),
    },
    example: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(20),
      color: theme.colors.text,
      fontStyle: 'italic',
    },
  });

export default SaleLoopStep;
